/**
 * Query-key factory. Every cache entry is addressed through here so that
 * invalidation after mutations stays consistent (e.g. invalidate `keys.courses.all`
 * to refresh every course-related query).
 */
export const keys = {
  session: ["session"] as const,
  courses: {
    all: ["courses"] as const,
    list: (archived: boolean) => ["courses", "list", { archived }] as const,
    detail: (id: number) => ["courses", "detail", id] as const,
    roster: (id: number, section?: string | null) => ["courses", "roster", id, section ?? null] as const,
    instructors: (id: number) => ["courses", "instructors", id] as const,
    questions: (id: number, status: string) => ["courses", "questions", id, status] as const,
    joinPreview: (code: string) => ["courses", "join-preview", code.toUpperCase()] as const,
    invitations: (id: number) => ["courses", "invitations", id] as const,
    reviews: (id: number) => ["courses", "reviews", id] as const,
    invitation: (token: string) => ["courses", "invitation", token] as const,
  },
  assignments: {
    all: ["assignments"] as const,
    list: (courseId: number) => ["assignments", "list", courseId] as const,
    detail: (id: number) => ["assignments", "detail", id] as const,
    /** Lives under `assignments` so every assignment change refreshes the outline too. */
    outline: (courseId: number) => ["assignments", "outline", courseId] as const,
    shares: (id: number) => ["assignments", "shares", id] as const,
  },
  people: {
    all: ["people"] as const,
    list: (query: string) => ["people", "list", query] as const,
  },
  quizLibrary: ["quiz-library"] as const,
  tenantSettings: ["tenant-settings"] as const,
  accountSso: ["account-sso"] as const,
  guest: {
    preview: (token: string) => ["guest", "preview", token] as const,
    content: (token: string) => ["guest", "content", token] as const,
  },
  comments: {
    all: ["comments"] as const,
    list: (assignmentId: number) => ["comments", assignmentId] as const,
  },
  dashboard: {
    student: ["dashboard", "student"] as const,
    instructor: ["dashboard", "instructor"] as const,
  },
  analytics: {
    all: ["analytics"] as const,
    assignment: (id: number, section?: string | null) => ["analytics", "assignment", id, section ?? null] as const,
    assignmentStudent: (id: number, userId: number) => ["analytics", "assignment", id, "student", userId] as const,
    course: (id: number, section?: string | null) => ["analytics", "course", id, section ?? null] as const,
    courseStudent: (id: number, userId: number) => ["analytics", "course", id, "student", userId] as const,
  },
} as const;
